import {cloneDeep} from 'lodash';
import _ from 'underscore';
import {parseString, processors} from 'xml2js';

import { AuthenticationTypeResolutionService, SiteDefaultAuth } from '~src/authentication/services/AuthenticationTypeResolutionService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';

import {getAppStore} from '../../../bootstrap/redux/store/AppStore';
import {Messages} from '../../../codegen/Localize';
import {FeatureFlags} from '../../../commonInfra/featureFlags/FeatureFlags';
import {IResult} from '../../../httpClient/serverApi/types/IResponse';
import {RequestHelper} from '../../../httpClient/utils/RequestHelper';
import {ResultHelper} from '../../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import {
  getSessionManagementServiceInstance,
} from '../../../sessionManagement/SessionManagementService';
import {SiteUserAuthSetting} from '../../../userManagement/users/serverApi/types/ISiteUser';
import {
  UpdateUserAuthSettingsRequest
} from '../../../userSettings/serverApi/requests/UpdateUserAuthSettingsRequest';
import {ServerSettingsUnauthenticated} from '../../services/ServerSettingsUnauthenticated';
import {omittedClientSecret} from '../directives/OIDCAuthenticationProvider';
import {
  CreateOrUpdateSiteOIDCConfigurationRequest,
} from '../serverApi/requests/CreateOrUpdateSiteOIDCConfigurationRequest';
import { CreateOrUpdateSiteScimConfigurationRequest } from '../serverApi/requests/CreateOrUpdateSiteScimConfigurationRequest';
import {DeleteOIDCConfigurationRequest} from '../serverApi/requests/DeleteOIDCConfigurationRequest';
import {DeleteSAMLConfigurationRequest} from '../serverApi/requests/DeleteSAMLConfigurationRequest';
import {
  GetAllOIDCConfigurationsRequest
} from '../serverApi/requests/GetAllOIDCConfigurationsRequest';
import {
  GetAllSAMLConfigurationsRequest
} from '../serverApi/requests/GetAllSAMLConfigurationsRequest';
import {
  GetOrCreateSiteOIDCConfigurationViewModelRequest,
  ISiteOIDCConfigurationViewModel,
} from '../serverApi/requests/GetOrCreateSiteOIDCConfigurationViewModelRequest';
import {
  GetSiteSAMLAuthSettingForEmbeddedViewsRequest,
  IGetSiteSAMLAuthSettingForEmbeddedViewsResult
} from '../serverApi/requests/GetSiteSAMLAuthSettingForEmbeddedViewsRequest';
import {
  GetSiteSAMLConfigurationRequest
} from '../serverApi/requests/GetSiteSAMLConfigurationRequest';
import {
  GetSiteSAMLConfigurationViewModelRequest
} from '../serverApi/requests/GetSiteSAMLConfigurationViewModelRequest';
import { GetSiteScimConfigurationRequest } from '../serverApi/requests/GetSiteScimConfigurationRequest';
import {RemoveIdentityProviderRequest} from '../serverApi/requests/RemoveIdentityProviderRequest';
import {SetSiteAttributeMappingRequest} from '../serverApi/requests/SetSiteAttributeMapping';
import {
  SetSiteSAMLAllowEmbeddedAuthenticationRequest,
} from '../serverApi/requests/SetSiteSAMLAllowEmbeddedAuthenticationRequest';
import {
  SetSiteSAMLAuthSettingForEmbeddedViewsRequest,
} from '../serverApi/requests/SetSiteSAMLAuthSettingForEmbeddedViewsRequest';
import {SetSiteSAMLEnabledRequest} from '../serverApi/requests/SetSiteSAMLEnabledRequest';
import {ISiteOIDCConfiguration} from '../serverApi/types/ISiteOIDCConfiguration';
import {
  ISiteSAMLAttributeMapping,
  ISiteSAMLConfiguration
} from '../serverApi/types/ISiteSAMLConfiguration';
import { ISiteScimConfiguration } from '../serverApi/types/ISiteScimConfiguration';

export interface IAuthenticationAttributeMapping {
  emailMapping: string;
  firstNameMapping: string;
  lastNameMapping: string;
  fullNameMapping: string;
  useFullName: boolean;
  emailAddressMapping: string;
}

export interface IAuthenticationProvider {
  allowEmbeddedAuthentication: boolean;
  id: string;
  isEditable: boolean;
  isEnabled: boolean;
  isLogoutSupported: boolean;
  testLoginUrl: string;
  type: string;
  idpConfigurationId?: string;
  idpConfigurationName?: string;
  alias?: string;
}

export const AuthenticationProviderType = {
  default: 'DEFAULT',
  oidc: 'OIDC',
  saml: 'SAML',
  tabidmfa: 'TABID_WITH_MFA',
};

export const AuthenticationProviderAlias = {
  google: 'Google',
  salesforce: 'Salesforce',
  openIdConnect: 'OpenId Connect'
};

export const SAML2_POST_BINDING_URI = 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST';

export interface IOIDCAuthenticationProvider extends IAuthenticationProvider {
  alias: string;
  salesforceDomain?: string;
  clientId?: string;
  clientSecret?: string;
  configurationUrl?: string;
  emailMapping?: string;
  fullNameMapping?: string;
  firstNameMapping?: string;
  lastNameMapping?: string;
  useFullName?: boolean;
  idpConfigurationId?: string;
  idpConfigurationName?: string;
  emailAddressMapping?: string;
}

export interface ISAMLAuthenticationProvider extends IAuthenticationProvider {
  acsUrl: string;
  attributeMapping: IAuthenticationAttributeMapping;
  certificateUrl: string;
  identityProviderEntityId: string;
  serviceProviderEntityId: string;
  ssoServiceURL: string;
  idpConfigurationId?: string;
  idpConfigurationName?: string;
}

export interface ISiteAuthenticationSettings {
  defaultProvider: IAuthenticationProvider;
  providerIdForEmbeddedViews: string; // null means: "let the user decide which provider to use"
  externalProviders: IAuthenticationProvider[];
  tabIdMfaProvider: IAuthenticationProvider;
  allProviders?: IAuthenticationProvider[];
}

export interface IIdPMetadataFields {
  identityProviderEntityId: string;
  ssoServiceURL: string;
}

interface ISiteAuthenticationSettingsInternal extends ISiteAuthenticationSettings {
  samlConfiguration: ISiteSAMLConfiguration;
  oidcViewModel: ISiteOIDCConfigurationViewModel;
}

const DefaultAuthenticationProviderId = 'DEFAULT';
const DEFAULT_IDP_CONFIG_ID_SAML = '11111111-1111-1111-1111-111111111111';
const DEFAULT_IDP_CONFIG_ID_OIDC = '00000000-0000-0000-0000-000000000000';

export const DefaultAuthenticationProvider: IAuthenticationProvider = {
  allowEmbeddedAuthentication: false,
  id: DefaultAuthenticationProviderId,
  isEditable: false,
  isEnabled: true,
  isLogoutSupported: true,
  testLoginUrl: null,
  type: AuthenticationProviderType.default,
};

const TabIdWithMFAAuthenticationProviderId = 'TABID_WITH_MFA';
export const TabIdWithMFAAuthenticationProvider: IAuthenticationProvider = {
  allowEmbeddedAuthentication: false,
  id: TabIdWithMFAAuthenticationProviderId,
  isEditable: false,
  isEnabled: false,
  isLogoutSupported: true,
  testLoginUrl: null,
  type: AuthenticationProviderType.tabidmfa,
};

export const AuthenticationSettingsWhenDisabled: ISiteAuthenticationSettings = {
  defaultProvider: DefaultAuthenticationProvider,
  providerIdForEmbeddedViews: null,
  externalProviders: [],
  tabIdMfaProvider: null
};

export function is3rdParty(authenticationProviderId: string): boolean {
  return authenticationProviderId !== DefaultAuthenticationProviderId && authenticationProviderId !== TabIdWithMFAAuthenticationProviderId;
}

const OpenIdConnect = 'OpenId Connect';

// shim needed to translate between the new SiteAuthenticationSettings client API above and the existing SAML/OIDC server APIs
export namespace SiteAuthenticationSettingsService {

  let siteId: string;
  let siteAuthenticationSettingsInternalPromise: Promise<ISiteAuthenticationSettingsInternal>;

  export function isEnabled(): boolean {
    return ServerSettingsUnauthenticated.isSiteSamlEnabled();
  }

  export function getSiteAuthenticationSettings(refresh: boolean = false): Promise<ISiteAuthenticationSettings> {
    if (isEnabled()) {
      return getSiteAuthenticationSettingsInternal(refresh);
    } else {
      return Promise.resolve(AuthenticationSettingsWhenDisabled);
    }
  }

  // This Method is for Authentication Tab
  // Some API calls in this method require admin access
  export function getSiteAuthenticationSettingsForAuthenticationTab(refresh: boolean = false): Promise<ISiteAuthenticationSettings> {
    if (isEnabled()) {
      return Promise.all([
        getSiteAuthenticationSettingsInternal(refresh),
        getSiteSAMLAuthSettingForEmbeddedViews(),
      ]).then((results: any[]) => {
        const settings: ISiteAuthenticationSettingsInternal = results[0];
        const siteSAMLAuthSettingForEmbeddedViews: IGetSiteSAMLAuthSettingForEmbeddedViewsResult = results[1];
        const providerIdForEmbeddedViews = getProviderIdForEmbeddedViews(settings, siteSAMLAuthSettingForEmbeddedViews);
        return {
          ...settings,
          providerIdForEmbeddedViews,
        };
      });
    } else {
      return Promise.resolve(AuthenticationSettingsWhenDisabled);
    }
  }

  export function getDefaultProvider(): IAuthenticationProvider {
    return DefaultAuthenticationProvider;
  }

  export function getTabIdMFAProvider(): IAuthenticationProvider {
    return TabIdWithMFAAuthenticationProvider;
  }

  export function enableProvider(id: string, idpConfigurationId: string): Promise<void> {
    throwUnlessEnabled();
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const newSettings = cloneDeep(settings);
      const provider = this.findProvider(newSettings, id);
      let noChanges = true;
      let isGenericOIDC = false;

      _.each(newSettings.externalProviders, (externalProvider: IAuthenticationProvider) => {
        const shouldBeEnabled = (externalProvider.id === id);
        noChanges = noChanges && (externalProvider.isEnabled === shouldBeEnabled);
        externalProvider.isEnabled = shouldBeEnabled;
      });
      if (noChanges) {
        return Promise.resolve();
      }
      newSettings.oidcViewModel.configuration.idpConfigurationId = null;
      newSettings.oidcViewModel.configuration.idpConfigurationName = null;
      switch (provider.type) {
        case AuthenticationProviderType.saml:
          newSettings.samlConfiguration.enabled = true;
          newSettings.oidcViewModel.configuration.enabled = false;
          break;
        case AuthenticationProviderType.oidc:
          newSettings.samlConfiguration.enabled = false;
          newSettings.oidcViewModel.configuration.enabled = true;
          isGenericOIDC = (<IOIDCAuthenticationProvider>provider).alias === OpenIdConnect && FeatureFlags.genericOIDCEnabled;
          if (isGenericOIDC) {
            newSettings.oidcViewModel.configuration.knownProviderAlias = null;
          } else {
            newSettings.oidcViewModel.configuration.knownProviderAlias = (<IOIDCAuthenticationProvider>provider).alias;
            newSettings.oidcViewModel.configuration.salesforceDomain = (<IOIDCAuthenticationProvider>provider).salesforceDomain;
          }
          break;
        case AuthenticationProviderType.default:
          newSettings.samlConfiguration.enabled = false;
          newSettings.oidcViewModel.configuration.enabled = false;
          break;
        default:
          throw new Error(`Unsupported provider type ${provider.type}`);
      }
      return Promise.all([
        WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled, idpConfigurationId: null })),
        WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration }))
      ]).then(() => {
        Object.assign(settings, newSettings);
      });
    });
  }

  export function enableProviderForMultipleIdP(id: string, idpConfigurationId: string): Promise<void> {
    throwUnlessEnabled();
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const newSettings = cloneDeep(settings);
      const provider = this.findProvider(newSettings, id);
      let noChanges = true;
      let isGenericOIDC = false;

      _.each(newSettings.externalProviders, (externalProvider: IAuthenticationProvider) => {
        const shouldBeEnabled = (externalProvider.id === id);
        noChanges = noChanges && (externalProvider.isEnabled === shouldBeEnabled);
        externalProvider.isEnabled = shouldBeEnabled;
      });
      if (noChanges) {
        return Promise.resolve();
      }
      switch (provider.type) {
        case AuthenticationProviderType.saml:
          newSettings.samlConfiguration.enabled = true;
          break;
        case AuthenticationProviderType.oidc:
          newSettings.oidcViewModel.configuration.enabled = true;
          isGenericOIDC = (<IOIDCAuthenticationProvider>provider).alias === OpenIdConnect && FeatureFlags.genericOIDCEnabled;
          if (isGenericOIDC) {
            newSettings.oidcViewModel.configuration.knownProviderAlias = null;
          } else {
            newSettings.oidcViewModel.configuration.knownProviderAlias = (<IOIDCAuthenticationProvider>provider).alias;
            newSettings.oidcViewModel.configuration.salesforceDomain = (<IOIDCAuthenticationProvider>provider).salesforceDomain;
          }
          break;
        case AuthenticationProviderType.default:
          break;
        default:
          throw new Error(`Unsupported provider type ${provider.type}`);
      }
      return Promise.all([
        WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled, idpConfigurationId: idpConfigurationId })),
        WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration }))
      ]).then(() => {
        Object.assign(settings, newSettings);
      });
    });
  }

  export function enableProvider2(id: string): Promise<void> {
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const newSettings = cloneDeep(settings);
      const provider = this.findProvider(newSettings, id);
      let noChanges = true;

      // enable and disable the correct provider for the internal settings
      _.each(newSettings.externalProviders, (externalProvider: IAuthenticationProvider) => {
        const shouldBeEnabled = (externalProvider.id === id);
        noChanges = noChanges && (externalProvider.isEnabled === shouldBeEnabled);
        externalProvider.isEnabled = shouldBeEnabled;
      });
      // don't make any disabling calls if we are on the same auth type
      if (noChanges) {
        return Promise.resolve();
      }
      newSettings.oidcViewModel.configuration.idpConfigurationId = null;
      newSettings.oidcViewModel.configuration.idpConfigurationName = null;
      // send any disable calls
      switch (provider.type) {
        case AuthenticationProviderType.saml:
          newSettings.samlConfiguration.enabled = true;
          newSettings.oidcViewModel.configuration.enabled = false;
          return WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration })).then(() => {
            Object.assign(settings, newSettings);
          });
        case AuthenticationProviderType.oidc:
          newSettings.samlConfiguration.enabled = false;
          return WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled })).then(() => {
            Object.assign(settings, newSettings);
          });
        case AuthenticationProviderType.default:
          newSettings.samlConfiguration.enabled = false;
          newSettings.oidcViewModel.configuration.enabled = false;
          return Promise.all([
            WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled })),
            WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration }))
          ]).then(() => {
            Object.assign(settings, newSettings);
          });
        default:
          throw new Error(`Unsupported provider type ${provider.type}`);
      }
    });

  }

  export function enableProviderForMultipleIdp2(id: string, idpConfigurationId: string): Promise<void> {
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const newSettings = cloneDeep(settings);
      const provider = this.findProvider(newSettings, id);
      let noChanges = true;

      // enable and disable the correct provider for the internal settings
      _.each(newSettings.externalProviders, (externalProvider: IAuthenticationProvider) => {
        const shouldBeEnabled = (externalProvider.id === id);
        noChanges = noChanges && (externalProvider.isEnabled === shouldBeEnabled);
        externalProvider.isEnabled = shouldBeEnabled;
      });
      // don't make any disabling calls if we are on the same auth type
      if (noChanges) {
        return Promise.resolve();
      }
      // send any disable calls
      switch (provider.type) {
        case AuthenticationProviderType.saml:
          newSettings.samlConfiguration.enabled = true;
          return WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration })).then(() => {
            Object.assign(settings, newSettings);
          });
        case AuthenticationProviderType.oidc:
          return WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled, idpConfigurationId: FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? idpConfigurationId : null})).then(() => {
            Object.assign(settings, newSettings);
          });
        case AuthenticationProviderType.default:
          return Promise.all([
            WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled, idpConfigurationId: FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? idpConfigurationId : null })),
            WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration }))
          ]).then(() => {
            Object.assign(settings, newSettings);
          });
        default:
          throw new Error(`Unsupported provider type ${provider.type}`);
      }
    });
  }

  export function setProviderEnabled(id: string, enable: boolean): Promise<IResult> {
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const provider = _.find(settings.allProviders, (provider: IAuthenticationProvider) => (provider.id === id));

      switch (provider.type) {
        case AuthenticationProviderType.saml:
          let requestPromise = WebHttpClientWithErrorToastInstance.sendRequest(new SetSiteSAMLEnabledRequest({ enable: enable, idpConfigurationId: provider.idpConfigurationId }));
          requestPromise.then(() => {
            provider.isEnabled = enable;
          });
          return requestPromise;
        case AuthenticationProviderType.oidc:
          provider.isEnabled = enable;
          return saveOIDCProvider(provider as IOIDCAuthenticationProvider);
        default:
          throw new Error(`Provider type ${provider.type} cannot be enabled or disabled`);
      }
    });
  }

  export function handleSAMLConfigurationUpdate(samlConfiguration: ISiteSAMLConfiguration): Promise<boolean> {
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      settings.samlConfiguration = samlConfiguration;
      const updatedProvider = getSAMLProvider(samlConfiguration);
      const provider = findProvider(settings, updatedProvider.id);
      Object.assign(provider, updatedProvider);
      return true;
    });
  }

  export function removeSAMLMetadata(id: string, idpConfigurationId: string): Promise<boolean> {
    throwUnlessEnabled();
    return WebHttpClient.sendRequest(new RemoveIdentityProviderRequest(idpConfigurationId)).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  export function uploadSAMLMetadata(id: string, samlMetadata: File): Promise<boolean> {
    throwUnlessEnabled();
    if (samlMetadata) {
      return getSessionManagementServiceInstance().uploadSAMLMetadata(samlMetadata).then((samlConfiguration: ISiteSAMLConfiguration) => {
        return handleSAMLConfigurationUpdate(samlConfiguration);
      });
    } else {
      return Promise.resolve(true);
    }
  }

  export function createOrUpdateSAMLConfiguration(provider: ISAMLAuthenticationProvider, samlMetadata: File): Promise<boolean> {
    throwUnlessEnabled();
    return getSessionManagementServiceInstance().createOrUpdateSAMLConfiguration(provider, samlMetadata)
    .then((samlConfiguration: ISiteSAMLConfiguration) => handleSAMLConfigurationUpdate(samlConfiguration));
  }

  export async function extractSAMLMetadataXml(samlMetadata: File): Promise<IIdPMetadataFields> {
    // set file to string then to xml
    let samlMetadataString;
    let singleSignOnServiceURL;
    let entityId;

    samlMetadataString = await readFileContent(samlMetadata);
    parseString(samlMetadataString, { tagNameProcessors: [processors.stripPrefix] },(err, result) => {
      entityId = result?.['EntityDescriptor']?.['$']?.['entityID'];
      let singleSignOnService = result?.['EntityDescriptor']?.['IDPSSODescriptor']?.[0]?.['SingleSignOnService'];
      singleSignOnService.forEach((el: any) => {
        if (el?.['$']?.['Binding'] === SAML2_POST_BINDING_URI) {
          singleSignOnServiceURL = el?.['$']?.['Location'];
        }
      });
    });
    return { ssoServiceURL: singleSignOnServiceURL, identityProviderEntityId: entityId };
  }

  export async function readFileContent(samlMetadata: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(samlMetadata,'xml');

      reader.onload = function() {
        const fileContents = reader.result as string;
        resolve(fileContents);
      };

      reader.onerror = function() {
        reject(new Error('File could not be read.'));
      };
    });
  }

  export function getSiteScimConfiguration(): Promise<ISiteScimConfiguration> {
    return WebHttpClient.sendRequest(new GetSiteScimConfigurationRequest());
  }

  export function createOrUpdateScimConfiguration(siteScimConfiguration: ISiteScimConfiguration): Promise<IResult> {
    return WebHttpClient.sendRequest(new CreateOrUpdateSiteScimConfigurationRequest({ siteScimConfiguration }));
  }

  export function setSAMLAttributeMapping(id: string, siteSAMLAttributeMapping: ISiteSAMLAttributeMapping, idpConfigurationId: string,
                                          idpConfigurationName: string): Promise<boolean> {
    throwUnlessEnabled();
    return WebHttpClient.sendRequest(new SetSiteAttributeMappingRequest({siteSAMLAttributeMapping: siteSAMLAttributeMapping, idpConfigurationId: FeatureFlags.featureFlagForMultipleIDPSupportEnabled? idpConfigurationId : null,
      idpConfigurationName: FeatureFlags.featureFlagForMultipleIDPSupportEnabled? idpConfigurationName : null})).then(() => {
      return WebHttpClient.sendRequest(new GetSiteSAMLConfigurationRequest());
    }).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  function getSiteSAMLConfigurationViewModel(idpConfigurationId: string): Promise<ISiteSAMLConfiguration>{
    return WebHttpClient.sendRequest(new GetSiteSAMLConfigurationViewModelRequest(idpConfigurationId));
  }

  export function deleteProvider(provider: IAuthenticationProvider): Promise<void> {
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const providerRef = _.find(settings.allProviders, (p: IAuthenticationProvider) => (p.idpConfigurationId === provider.idpConfigurationId));
      // create a copy of allProviders that does not include the deleted provider
      const newSettings = cloneDeep(settings);
      newSettings.allProviders = newSettings.allProviders.filter((p: IAuthenticationProvider) => p.idpConfigurationId !== provider.idpConfigurationId);

      switch (providerRef.type) {
        case AuthenticationProviderType.saml:
          return WebHttpClientWithErrorToastInstance.sendRequest(new DeleteSAMLConfigurationRequest({idpConfigurationId: providerRef.idpConfigurationId})).then(() => {
            Object.assign(settings, newSettings);
          });
        case AuthenticationProviderType.oidc:
          return WebHttpClientWithErrorToastInstance.sendRequest(new DeleteOIDCConfigurationRequest({idpConfigurationId: providerRef.idpConfigurationId})).then(() => {
            Object.assign(settings, newSettings);
          });
        default:
          throw new Error(`Deleting provider of type ${provider.type} is not supported`);
      }
    });
  }

  // should be removed by TFSID:607630
  export function getAuthSetting(id: string, settings: ISiteAuthenticationSettings): string {
    const provider = FeatureFlags.featureFlagForMultipleIDPSupportEnabled? findProviderForMultipleIdp(settings, id, id) : findProvider(settings, id);
    if (!provider) {
      throw new Error(`Provider with id ${id} doesn't exist`);
    }
    switch (provider.type) {
      case AuthenticationProviderType.saml:
        return SiteUserAuthSetting.Saml;
      case AuthenticationProviderType.oidc:
        return SiteUserAuthSetting.Oidc;
      case AuthenticationProviderType.default:
        return SiteUserAuthSetting.Default;
      case AuthenticationProviderType.tabidmfa:
        return SiteUserAuthSetting.TabId_with_MFA;
      default:
        throw new Error(`Unsupported provider type ${provider.type}`);
    }
  }

  export function setProviderIdForEmbeddedViews(id: string): Promise<void> {
    throwUnlessEnabled();
    return getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = (id ? getAuthSetting(id, settings) : null);
      return WebHttpClient.sendRequest(new SetSiteSAMLAuthSettingForEmbeddedViewsRequest({ authSetting: authSetting })).then(() => {
        settings.providerIdForEmbeddedViews = id;
      });
    });
  }

  export async function setProviderIdForEmbeddedViewsForMultipleIdp(id: string): Promise<void> {
    throwUnlessEnabled();
    await WebHttpClient.sendRequest(new SetSiteSAMLAuthSettingForEmbeddedViewsRequest({ idpConfigurationId: id }));
  }

  export function setProviderIdForUsers(id: string, userIds: string[], idpConfigurationName: string, idpConfigurationId: string): Promise<IResult> {
    throwUnlessEnabled();
    return getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = getAuthSetting(id, settings);
      const requests = RequestHelper.buildRequests(userIds, (userIds => new UpdateUserAuthSettingsRequest(userIds, authSetting, idpConfigurationName, idpConfigurationId)));
      return WebHttpClient.sendRequestsParallel(requests, ResultHelper.combineResults);
    });
  }

  export function setAllowEmbeddedAuthentication(id: string, allow: boolean, idpConfigurationId: string): Promise<boolean> {
    throwUnlessEnabled();
    return WebHttpClient.sendRequest(new SetSiteSAMLAllowEmbeddedAuthenticationRequest({ allow: allow, idpConfigurationId: idpConfigurationId })).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  // Decides what name to display for the provider in the user grid
  export function getProviderDisplayName(provider: IAuthenticationProvider): string {
    switch (provider.type) {
      case AuthenticationProviderType.oidc:
        return getDisplayNameForOIDCProvider(<IOIDCAuthenticationProvider>provider);
      case AuthenticationProviderType.saml:
        return getDisplayNameForSAMLProvider(<ISAMLAuthenticationProvider>provider);
      case AuthenticationProviderType.default:
        return getDisplayNameForDefaultProvider(provider);
      case AuthenticationProviderType.tabidmfa:
        return getDisplayNameForTOLWithMFAProvider();
      default:
        throw new Error(`Unsupported provider type ${provider.type}`);
    }
  }

  export function findProvider(settings: ISiteAuthenticationSettings, id: string) {
    if (id === settings.defaultProvider.id) {
      return settings.defaultProvider;
    }
    if (id === settings.tabIdMfaProvider.id) {
      return settings.tabIdMfaProvider;
    }
    return _.find(settings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === id));
  }

  export function findProviderForMultipleIdp(settings: ISiteAuthenticationSettings, id: string, idpConfigurationId: string) {
    if (id === settings.defaultProvider.id) {
      return settings.defaultProvider;
    }
    if (id === settings.tabIdMfaProvider.id) {
      return settings.tabIdMfaProvider;
    }
    return _.find(settings.allProviders, (provider: IAuthenticationProvider) => (provider.id === idpConfigurationId));
  }

  export function isAuthenticationSSO(settings: ISiteAuthenticationSettings) {
    if (!settings) {
      return false;
    }
    const providers = FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? settings.allProviders : settings.externalProviders;
    return providers && !!_.find(providers, (provider: IAuthenticationProvider) => (provider.isEnabled));
  }

  // should be removed by TFSID:607630
  export function getAuthSettingToProviderIdMap(): Promise<_.Dictionary<string>> {
    if (!isEnabled()) {
      const map = {
        [SiteUserAuthSetting.Default]: DefaultAuthenticationProvider.id
      };
      return Promise.resolve(map);
    }

    if (!FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
      return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
        return getAuthSettingToProviderIdMapInternal(settings.oidcViewModel);
      });
    }

    return Promise.all([
      WebHttpClient.sendRequest(new GetAllOIDCConfigurationsRequest()),
      WebHttpClient.sendRequest(new GetAllSAMLConfigurationsRequest()),
    ]).then((results: any[]) => {
      let allOidcViewModels: ISiteOIDCConfigurationViewModel[] = results[0];
      let allSamlConfigurations: ISiteSAMLConfiguration[] = results[1].errors? [] : results[1];
      let allProviders: IAuthenticationProvider[] = getAllBuiltinOIDCProviders(allOidcViewModels);
      allProviders = allProviders.concat(getAllSAMLProviders(allSamlConfigurations));
      return getAuthSettingToProviderIdMapInternalForAllProviders(allProviders);
    });
  }

  interface OIDCConfigurationMetadata {
    authorizationEndpoint: string;
    tokenEndpoint: string;
    userinfoEndpoint: string;
    jwksUri: string;
    endSessioEndpoint: string;
  }

  function getOIDCConfigurationMetadata(url: string): Promise<OIDCConfigurationMetadata> {
    return fetch(url)
    .then((response) => response.json())
    .then((responseJson) => {
      return {
        authorizationEndpoint: responseJson.authorization_endpoint,
        tokenEndpoint: responseJson.token_endpoint,
        userinfoEndpoint: responseJson.userinfo_endpoint,
        jwksUri: responseJson.jwks_uri,
        endSessioEndpoint: responseJson.end_session_endpoint
      };
    });
  }

  function sendCreateOrUpdateSiteOIDCConfigurationRequest(provider: IOIDCAuthenticationProvider,
                                                          metadata?: OIDCConfigurationMetadata): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({
      siteOIDCConfiguration: {
        enabled: provider.isEnabled,
        testLoginUrl: provider.testLoginUrl,
        knownProviderAlias: metadata == null ? provider.alias : null,
        salesforceDomain: provider.salesforceDomain,
        allowEmbeddedAuthentication: provider.allowEmbeddedAuthentication,
        clientId: provider.clientId,
        clientSecret: provider.clientSecret === omittedClientSecret ? null : provider.clientSecret,
        discoveryEndpoint: provider.configurationUrl,
        authorizationEndpoint: metadata == null ? null : metadata.authorizationEndpoint,
        tokenEndpoint: metadata == null ? null : metadata.tokenEndpoint,
        userinfoEndpoint: metadata == null ? null : metadata.userinfoEndpoint,
        jwksUri: metadata == null ? null : metadata.jwksUri,
        endSessionEndpoint: metadata == null ? null : metadata.endSessioEndpoint,
        emailMapping: provider.emailMapping,
        emailAddressMapping: provider.emailAddressMapping,
        firstNameMapping: provider.firstNameMapping,
        lastNameMapping: provider.lastNameMapping,
        fullNameMapping: provider.fullNameMapping,
        useFullName: provider.useFullName,
        idpConfigurationId: FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? provider.idpConfigurationId : null,
        idpConfigurationName: FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? provider.idpConfigurationName : null
      }
    }));
  }

  export function saveOIDCProvider(provider: IOIDCAuthenticationProvider): Promise<IResult> {
    if(typeof provider.configurationUrl != null && provider.configurationUrl) {
      return getOIDCConfigurationMetadata(provider.configurationUrl)
      .then((metadata) => {
        if (!provider.isLogoutSupported) {
          metadata.endSessioEndpoint = null;
        }
        return sendCreateOrUpdateSiteOIDCConfigurationRequest(provider, metadata);
      });
    }

    return sendCreateOrUpdateSiteOIDCConfigurationRequest(provider);
  }


  function throwUnlessEnabled() {
    if (!isEnabled()) {
      throw new Error('Site authentication settings are not enabled');
    }
  }

  function getSiteAuthenticationSettingsInternal(refresh: boolean = false): Promise<ISiteAuthenticationSettingsInternal> {
    // Since only React components may subscribe to the app store, manually refresh to check for change before sending requests
    checkIfAppStateChanged();

    if (!siteAuthenticationSettingsInternalPromise || refresh) {
      siteAuthenticationSettingsInternalPromise = getSiteAuthenticationSettingsInternalImpl();
    }
    return siteAuthenticationSettingsInternalPromise;
  }

  function getAuthSettingToProviderIdMapInternal(oidcViewModel: ISiteOIDCConfigurationViewModel): _.Dictionary<string> {
    // keys: siteUserAuthSetting, values: authentication provider id
    const map = {
      [SiteUserAuthSetting.Oidc]: getOIDCProviderId(oidcViewModel.configuration.knownProviderAlias),
      //+ ':' +oidcViewModel.configuration.idpConfigurationName,
      [SiteUserAuthSetting.Saml]: getSAMLProviderId(),
      [SiteUserAuthSetting.Default]: DefaultAuthenticationProvider.id,
      [SiteUserAuthSetting.TabId_with_MFA]: TabIdWithMFAAuthenticationProvider.id
    };
    return map;
  }

  function getAuthSettingToProviderIdMapInternalForAllProviders(allProviders: IAuthenticationProvider[]): _.Dictionary<string> {
    // keys: siteUserAuthSetting, values: authentication provider id
    const map = {
      [SiteUserAuthSetting.Default]: DefaultAuthenticationProvider.id,
      [SiteUserAuthSetting.TabId_with_MFA]: TabIdWithMFAAuthenticationProvider.id
    };
    for (let provider of allProviders) {
      switch (provider.type) {
        case AuthenticationProviderType.saml:
          map[provider.id + ':' + provider.type] = getSAMLProviderId();
          break;
        case AuthenticationProviderType.oidc:
          map[provider.id + ':' + provider.type] = getOIDCProviderId(provider.alias);
      }
    }
    map[SiteUserAuthSetting.Default] = DefaultAuthenticationProvider.id;
    map[SiteUserAuthSetting.TabId_with_MFA] = TabIdWithMFAAuthenticationProvider.id;
    return map;
  }

  function getSiteAuthenticationSettingsInternalImpl(): Promise<ISiteAuthenticationSettingsInternal> {
    return Promise.all([
      WebHttpClient.sendRequest(new GetSiteSAMLConfigurationRequest()).catch(() => {
        /* Don't fail if the user has no permission */
        return {};
      }),
      WebHttpClient.sendRequest(new GetOrCreateSiteOIDCConfigurationViewModelRequest()),
      FeatureFlags.featureFlagForMultipleIDPSupportEnabled? WebHttpClient.sendRequest(new GetAllOIDCConfigurationsRequest()): [],
      FeatureFlags.featureFlagForMultipleIDPSupportEnabled? WebHttpClient.sendRequest(new GetAllSAMLConfigurationsRequest()): [],
    ]).then((results: any[]) => {
      const samlConfiguration: ISiteSAMLConfiguration = results[0];
      const oidcViewModel: ISiteOIDCConfigurationViewModel = results[1];
      let allOidcViewModels: ISiteOIDCConfigurationViewModel[] = results[2];
      let allSamlConfigurations: ISiteSAMLConfiguration[] = results[3].errors? [samlConfiguration] : results[3];
      const externalProviders: IAuthenticationProvider[] = getBuiltinOIDCProviders(oidcViewModel);
      const samlProvider: ISAMLAuthenticationProvider = getSAMLProvider(samlConfiguration);
      let allProviders: IAuthenticationProvider[];
      if (FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
        allOidcViewModels = filterAllOIDCConfigurations(allOidcViewModels);
        allProviders = getAllBuiltinOIDCProviders(allOidcViewModels);
        allSamlConfigurations = filterAllSAMLConfigurations(allSamlConfigurations);
        allProviders = allProviders.concat(getAllSAMLProviders(allSamlConfigurations));
      }
      externalProviders.push(samlProvider);

      if (FeatureFlags.genericOIDCEnabled) {
        const oidcProvider: IOIDCAuthenticationProvider = getOIDCProvider(oidcViewModel.configuration);
        externalProviders.push(oidcProvider);
      }

      const defaultProvider = DefaultAuthenticationProvider;
      const tabIdMfaProvider = TabIdWithMFAAuthenticationProvider;
      if (getSessionManagementServiceInstance().isMfaEnabled()) {
        tabIdMfaProvider.isEnabled = true;
        if (getSessionManagementServiceInstance().isMfaRequiredForSite()) {
          defaultProvider.isEnabled = false;
        }
      } else {
        tabIdMfaProvider.isEnabled = false;
      }
      const authSettingToProviderIdMap = getAuthSettingToProviderIdMapInternal(oidcViewModel);

      return {
        defaultProvider: defaultProvider,
        externalProviders: externalProviders,
        tabIdMfaProvider: tabIdMfaProvider,
        providerIdForEmbeddedViews: authSettingToProviderIdMap[samlConfiguration.authSettingForEmbeddedViews],
        samlConfiguration: samlConfiguration,
        oidcViewModel: oidcViewModel,
        allProviders: allProviders
      };
    });
  }

  function getSiteSAMLAuthSettingForEmbeddedViews(): Promise<IGetSiteSAMLAuthSettingForEmbeddedViewsResult> {
    if (!FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
      return Promise.resolve({});
    }

    return WebHttpClient.sendRequest(new GetSiteSAMLAuthSettingForEmbeddedViewsRequest()).catch(() => {
      return {
        errors: [
          {
            code: 0,
          },
        ],
      };
    });
  }

  function getProviderIdForEmbeddedViews(
    siteAuthenticationSettings: ISiteAuthenticationSettingsInternal,
    siteSAMLAuthSettingForEmbeddedViews: IGetSiteSAMLAuthSettingForEmbeddedViewsResult) {
    if (!FeatureFlags.featureFlagForMultipleIDPSupportEnabled) {
      return siteAuthenticationSettings.providerIdForEmbeddedViews;
    }

    if (siteSAMLAuthSettingForEmbeddedViews.errors) {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.siteAuthenticationEmbeddedTypeGetError() });
      return null;
    }

    // Empty authenticationSettingForEmbeddedViz means embedded setting is not set after Multiple IDP project is launched
    // Use auth setting for embedded views
    if (!siteSAMLAuthSettingForEmbeddedViews.authenticationSettingForEmbeddedViz) {
      const embeddedAuthSetting = siteAuthenticationSettings.samlConfiguration.authSettingForEmbeddedViews;
      switch (embeddedAuthSetting) {
        case AuthenticationProviderType.default:
        case AuthenticationProviderType.tabidmfa:
          return embeddedAuthSetting;
        case AuthenticationProviderType.saml:
          return DEFAULT_IDP_CONFIG_ID_SAML;
        case AuthenticationProviderType.oidc:
          return DEFAULT_IDP_CONFIG_ID_OIDC;
        default:
          return null;
      }
    }

    return siteSAMLAuthSettingForEmbeddedViews.authenticationSettingForEmbeddedViz;
  }

  function getOIDCProviderId(alias: string) {
    if (alias != null) {
      return AuthenticationProviderType.oidc + ':' + alias;
    }
    return AuthenticationProviderType.oidc;
  }

  function getAllBuiltinOIDCProviders(allOidcViewModels: ISiteOIDCConfigurationViewModel[]): IOIDCAuthenticationProvider[] {
    return _.map(allOidcViewModels, (oidcViewModel: ISiteOIDCConfigurationViewModel) => {
      const configuration = oidcViewModel.configuration;
      return {
        allowEmbeddedAuthentication: false,
        id: configuration.idpConfigurationId,
        isEditable: false,
        isEnabled: configuration.enabled,
        isLogoutSupported: true,
        testLoginUrl: configuration.testLoginUrl,
        type: AuthenticationProviderType.oidc,
        alias: configuration.knownProviderAlias,
        salesforceDomain: configuration.knownProviderAlias === 'Salesforce' ? configuration.salesforceDomain : null,
        idpConfigurationId: configuration.idpConfigurationId,
        idpConfigurationName: configuration.idpConfigurationName,
        clientId: configuration.clientId,
        clientSecret: configuration.clientSecret,
        configurationUrl: configuration.discoveryEndpoint,
        emailMapping: configuration.emailMapping,
        fullNameMapping: configuration.fullNameMapping,
        firstNameMapping: configuration.firstNameMapping,
        lastNameMapping: configuration.lastNameMapping,
        emailAddressMapping: configuration.emailAddressMapping
      };
    });
  }

  function getBuiltinOIDCProviders(oidcViewModel: ISiteOIDCConfigurationViewModel): IOIDCAuthenticationProvider[] {
    const oidcConfiguration = oidcViewModel.configuration;
    return _.map(oidcViewModel.knownProviderAliases, (knownProviderAlias: string) => {
      return {
        allowEmbeddedAuthentication: false,
        id: getOIDCProviderId(knownProviderAlias),
        isEditable: false,
        isEnabled: oidcConfiguration.enabled && oidcConfiguration.knownProviderAlias === knownProviderAlias,
        isLogoutSupported: true,
        testLoginUrl: oidcConfiguration.testLoginUrl,
        type: AuthenticationProviderType.oidc,

        alias: knownProviderAlias,
        salesforceDomain: knownProviderAlias === 'Salesforce' ? oidcConfiguration.salesforceDomain : null,
        idpConfigurationId: oidcConfiguration.idpConfigurationId,
        idpConfigurationName: oidcConfiguration.idpConfigurationName
      };
    });
  }

  function getOIDCProvider(configuration: ISiteOIDCConfiguration): IOIDCAuthenticationProvider {
    const provider: IOIDCAuthenticationProvider = {
      allowEmbeddedAuthentication: configuration.allowEmbeddedAuthentication,
      id: AuthenticationProviderType.oidc,
      isEditable: true,
      isEnabled: configuration.enabled && configuration.knownProviderAlias == null,
      isLogoutSupported: configuration.endSessionEndpoint != null,
      testLoginUrl: configuration.testLoginUrl,
      type: AuthenticationProviderType.oidc,
      alias: OpenIdConnect,
      clientId: configuration.clientId,
      clientSecret: configuration.clientSecret,
      configurationUrl: configuration.discoveryEndpoint,
      useFullName: configuration.useFullName,
      emailMapping: configuration.emailMapping,
      emailAddressMapping: configuration.emailAddressMapping,
      firstNameMapping: configuration.firstNameMapping,
      lastNameMapping: configuration.lastNameMapping,
      fullNameMapping: configuration.fullNameMapping,
      idpConfigurationId: configuration.idpConfigurationId,
      idpConfigurationName: configuration.idpConfigurationName
    };
    return provider;
  }

  function getSAMLProviderId() {
    return AuthenticationProviderType.saml;
  }

  function getAllSAMLProviders(samlConfigurations: ISiteSAMLConfiguration[]): ISAMLAuthenticationProvider[] {
    return _.map(samlConfigurations, (samlConfiguration: ISiteSAMLConfiguration) => {
      return {
        allowEmbeddedAuthentication: samlConfiguration.allowEmbeddedAuthentication,
        id: samlConfiguration.idpConfigurationId,
        isEditable: true,
        isEnabled: samlConfiguration.enabled,
        isLogoutSupported: samlConfiguration.isSingleLogoutSupported,
        testLoginUrl: samlConfiguration.testLoginUrl,
        type: AuthenticationProviderType.saml,

        acsUrl: samlConfiguration.acsUrl,
        attributeMapping: samlConfiguration.siteSAMLAttributeMapping,
        certificateUrl: samlConfiguration.certificateUrl,
        identityProviderEntityId: samlConfiguration.identityProviderEntityId,
        serviceProviderEntityId: samlConfiguration.serviceProviderEntityId,
        ssoServiceURL: samlConfiguration.ssoServiceURL,
        idpConfigurationId: samlConfiguration.idpConfigurationId,
        idpConfigurationName: samlConfiguration.idpConfigurationName
      };
    });
  }

  function getSAMLProvider(samlConfiguration: ISiteSAMLConfiguration): ISAMLAuthenticationProvider {
    return {
      allowEmbeddedAuthentication: samlConfiguration.allowEmbeddedAuthentication,
      id: getSAMLProviderId(),
      isEditable: true,
      isEnabled: samlConfiguration.enabled,
      isLogoutSupported: samlConfiguration.isSingleLogoutSupported,
      testLoginUrl: samlConfiguration.testLoginUrl,
      type: AuthenticationProviderType.saml,

      acsUrl: samlConfiguration.acsUrl,
      attributeMapping: samlConfiguration.siteSAMLAttributeMapping,
      certificateUrl: samlConfiguration.certificateUrl,
      identityProviderEntityId: samlConfiguration.identityProviderEntityId,
      serviceProviderEntityId: samlConfiguration.serviceProviderEntityId,
      ssoServiceURL: samlConfiguration.ssoServiceURL,
      idpConfigurationId: samlConfiguration.idpConfigurationId,
      idpConfigurationName: samlConfiguration.idpConfigurationName
    };
  }

  function getDisplayNameForSAMLProvider(provider: ISAMLAuthenticationProvider): string {
    const providerName = maybeGetHost(provider.ssoServiceURL);
    const translateKeyFn = providerName && !FeatureFlags.featureFlagForMultipleIDPSupportEnabled ? Messages.ssoAuthTypeSAML_name : Messages.ssoAuthTypeSAML_name_undefined;
    const translateValues = { PROVIDER_NAME: providerName };
    return translateKeyFn(translateValues);
  }

  function getDisplayNameForOIDCProvider(provider: IOIDCAuthenticationProvider): string {
    const translateKeyFn = provider.isEditable ? Messages.ssoAuthTypeOIDC_name_editable : Messages.ssoAuthTypeOIDC_name;
    const translateValues = { PROVIDER_NAME: provider.alias };
    return translateKeyFn(translateValues);
  }

  function getDisplayNameForDefaultProvider(provider: IAuthenticationProvider): string {
    let translatedDisplayName;
    if (ServerSettingsUnauthenticated.isTableauIdSupported()) {
      translatedDisplayName = Messages.ssoAuthTypeDefault_name_tableauid();
    } else {
      switch (AuthenticationTypeResolutionService.siteAuthDefault()) {
        case SiteDefaultAuth[SiteDefaultAuth.Local]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_local();
          break;
        case SiteDefaultAuth[SiteDefaultAuth.ActiveDirectory]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_ad();
          break;
        case SiteDefaultAuth[SiteDefaultAuth.SAML]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_saml();
          break;
        case SiteDefaultAuth[SiteDefaultAuth.External]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_external();
          break;
        default:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_other();
          break;
      }
    }
    return translatedDisplayName;
  }

  function getDisplayNameForTOLWithMFAProvider(): string {
    return Messages.ssoAuthTypeDefault_name_tableauid_mfa();
  }

  function maybeGetHost(urlString: string): string {
    const match = /\/\/.*?(\w+\.\w+)($|:|\/)/.exec(urlString);
    if (match) {
      return match[1];
    }
    return undefined;
  }

  function checkIfAppStateChanged() {
    const state = getAppStore().getState();
    const sessionSiteId = state && state.session && state.session.info && state.session.info.site.id;
    if (siteId !== sessionSiteId) {
      siteId = sessionSiteId;
      siteAuthenticationSettingsInternalPromise = null;
    }
  }

  // Only show configurations that have idpConfigurationNames set.
  // For existing sites, the configurations that are created by default after clicking Settings, we only show when those configurations are enabled atleast once
  // For new sites, the configurations that are created by default after clicking Settings, would not be shown because they won't have alias set.
  // If alias is not set, idpConfigurationName is null and we don't include such OIDC configurations in the UI
  function filterAllOIDCConfigurations(allOIDCViewModels: ISiteOIDCConfigurationViewModel[]): ISiteOIDCConfigurationViewModel[] {
    // TODO replace with _filter
    return allOIDCViewModels.filter((oidcViewModel) =>
      // Generic OIDC configurations don't have alias set. Also older generic OIDC configurations won't have idpConfigurationName set.
      oidcViewModel.configuration.idpConfigurationName != null || oidcViewModel.configuration.clientId != null);
  }

  // For existing sites, the configurations that are or were used before will be shown
  // For new sites, the configurations that are created by default after clicking Settings, would not be shown
  function filterAllSAMLConfigurations(allSAMLConfigurations: ISiteSAMLConfiguration[]): ISiteSAMLConfiguration[] {
    // TODO replace with _filter
    return allSAMLConfigurations.filter((samlConfiguration) => samlConfiguration.identityProviderEntityId != null);
  }
}

export type SiteAuthenticationSettingsService = typeof SiteAuthenticationSettingsService;
